import { sortBy } from "lodash";
import React, { useState } from "react";

import { filterMaybeArray, undefinedIfEmpty } from "@examspark/common";
import {
  useQueryGetUnitItems,
  useQueryListClassroomUnitActivities,
  useAppContext,
} from "@examspark/common-app";
import { Button } from "@examspark/common-ui";
import {
  CardAction,
  CardLink,
} from "@examspark/common-ui/dist/components/containers/LinkCard";
import {
  IconAddLine,
  IconAtomLine,
} from "@examspark/common-ui/dist/components/icons";
import { Maybe, ParsedClassroom, ParsedTCMPCUnit } from "@examspark/types";

import ClassroomActivityEditorModal from "../../activity/ClassroomActivityEditorModal";
import SidebarBox, { EmptySidebarBox } from "../../common/SidebarBox";
import UnitItemsModal from "../../unit/UnitItemsModal";
import PinToPlannerButton from "../PinToPlannerButton";

import { SidebarButtonItem, SidebarLinkItem } from "./BasicSidebarListItem";

const ClassroomActivities = (props: {
  classroom?: ParsedClassroom;
  unit?: Maybe<ParsedTCMPCUnit>;
}) => {
  const [isItemsModalOpen, setIsItemsModalOpen] = useState(false);
  const [isNewActivityModalOpen, setIsNewActivityModalOpen] = useState(false);
  const [context] = useAppContext();
  const classroomId = props.classroom?.id;
  const unitId = props.unit?.id;
  const { data: activities, refetch } = useQueryListClassroomUnitActivities(
    props.classroom?.workspaceId,
    props.classroom?.id,
    props?.unit?.id,
  );
  const { data: items } = useQueryGetUnitItems(
    context.workspaceUser?.id,
    props?.unit,
  );

  return (
    <>
      {props.unit && props.classroom && (
        <ClassroomActivityEditorModal
          classroom={props.classroom}
          unit={props.unit}
          isOpen={isNewActivityModalOpen}
          onRequestClose={() => setIsNewActivityModalOpen(false)}
          onSave={() => {
            refetch();
            setIsNewActivityModalOpen(false);
          }}
        />
      )}
      <UnitItemsModal
        isOpen={isItemsModalOpen}
        onRequestClose={() => setIsItemsModalOpen(false)}
        items={filterMaybeArray(items?.map((item) => item.resource)) ?? []}
      />
      <SidebarBox
        title="Assessments"
        toolbar={
          <>
            <Button
              disabled={
                !!props.classroom?.isArchived || !props?.unit || !items?.length
              }
              title="New Assessment"
              onClick={() => setIsNewActivityModalOpen(true)}
              icon={<IconAddLine />}
            />
          </>
        }
        footer={
          <SidebarButtonItem
            disabled={!items?.length}
            onClick={() => setIsItemsModalOpen(true)}
          >
            Preview Assessment Items
            {!!items?.length && ` (${items.length})`}
          </SidebarButtonItem>
        }
      >
        {!activities?.length && (
          <EmptySidebarBox>
            <IconAtomLine />
            <div>
              You don't have any Assessments for this Unit / Module yet.
            </div>
          </EmptySidebarBox>
        )}
        {activities != null && activities.length > 0 && (
          <>
            {sortBy(activities, "createdAt")
              .reverse()
              .map((act) => (
                <SidebarLinkItem key={act.examConfig?.id}>
                  <CardLink
                    to={`/classroom/${classroomId}/unit/${unitId}/activities/${act.examConfig?.id}`}
                  >
                    {undefinedIfEmpty(act.examConfig?.resource?.name) ??
                      "Untitled activity"}
                  </CardLink>
                  <CardAction>
                    {!!act.examConfig && (
                      <PinToPlannerButton resource={act.examConfig} />
                    )}
                  </CardAction>
                </SidebarLinkItem>
              ))}
          </>
        )}
      </SidebarBox>
    </>
  );
};

export default ClassroomActivities;
